'use client';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useGameSocket } from '../../hooks/Usegamesocket';
import Matchmaking from './Matchmaking';
import GameRoom from './GameRoom';

// ── Nickname helpers ──────────────────────────────────────────────────────
const LS_NICK    = 'cluecue_nickname';
const LS_STREAK  = 'win_streak';

function getStoredNick() {
  return localStorage.getItem(LS_NICK) || '';
}
function saveNick(n) {
  localStorage.setItem(LS_NICK, n);
}
function getStreak() {
  return parseInt(localStorage.getItem(LS_STREAK) || '0');
}
function incStreak() {
  const s = getStreak() + 1;
  localStorage.setItem(LS_STREAK, String(s));
  return s;
}
function resetStreak() {
  localStorage.setItem(LS_STREAK, '0');
}

export default function GamePage({ currentUser, onLeaveToApp }) {
  const [screen,   setScreen]   = useState('lobby');
  const [roomData, setRoomData] = useState(null);
  const [myInfo,   setMyInfo]   = useState(null);
  const [nickname, setNickname] = useState(getStoredNick);
  const [nickInput, setNickInput] = useState(getStoredNick);
  const [nickError, setNickError] = useState('');

  const gameEventHandler  = useRef(null);
  const pendingEventsRef  = useRef([]);

  const handleEvent = useCallback((event) => {
    switch (event.type) {
      case 'match_found': {
        setRoomData({
          roomId:   event.roomId,
          opponent: event.opponent,
          you:      event.you,
          p1:       event.p1,
          p2:       event.p2,
          onEvent:  (handler) => {
            gameEventHandler.current = handler;
            pendingEventsRef.current.forEach(e => handler(e));
            pendingEventsRef.current = [];
          },
        });
        setMyInfo(event.you);
        setScreen('game');
        break;
      }
      case 'queue_timeout': {
        setScreen('lobby');
        break;
      }
      case 'connection_lost':
      case 'connection_error': {
        if (screen === 'matchmaking') setScreen('lobby');
        break;
      }
      default: {
        if (gameEventHandler.current) {
          gameEventHandler.current(event);
        } else {
          pendingEventsRef.current.push(event);
        }
        break;
      }
    }
  }, [screen]);

  const { status, joinQueue, disconnect, emit } = useGameSocket({ onEvent: handleEvent });

  // Save nickname and use it as player name
  const handleSaveNick = () => {
    const cleaned = nickInput.replace(/[^a-zA-Z\s]/g, '').trim();
    if (cleaned.length < 2) { setNickError('At least 2 letters please'); return; }
    if (cleaned.length > 16) { setNickError('Max 16 characters'); return; }
    setNickError('');
    saveNick(cleaned);
    setNickname(cleaned);
  };

  const handleJoinQueue = () => {
    const streak = getStreak();
    const name = nickname || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'Player';
    setScreen('matchmaking');
    joinQueue(streak, name);
  };

  const handleCancelQueue = () => {
    disconnect();
    setScreen('lobby');
  };

  const handleLeaveGame = (reason, didWin) => {
    disconnect();
    if (didWin === true)  incStreak();
    if (didWin === false) resetStreak();

    if (reason === 'play_again') {
      handleJoinQueue();
    } else if (reason === 'quit' && onLeaveToApp) {
      // Go back to dashboard
      onLeaveToApp();
    } else {
      setScreen('lobby');
      setRoomData(null);
      gameEventHandler.current = null;
      pendingEventsRef.current = [];
    }
  };

  const streak = getStreak();
  const streakBadge = streak >= 10 ? '👑🔥💀' : streak >= 5 ? '👑🔥' : streak >= 3 ? '👑' : null;

  if (screen === 'matchmaking') return <Matchmaking onCancel={handleCancelQueue} />;

  if (screen === 'game' && roomData) {
    return (
      <GameRoom
        roomData={roomData}
        myInfo={myInfo}
        emit={emit}
        onLeave={handleLeaveGame}
      />
    );
  }

  // ── Lobby ────────────────────────────────────────────────────────────────
  return (
    <div style={styles.lobby}>
      <div style={styles.card}>
        <div style={styles.logoRow}>
          <h1 style={styles.title}>ClueCue</h1>
        </div>
        <p style={styles.subtitle}>
          Guess the celebrity, dish, country or brand from blurred clues before your opponent does
        </p>

        <div style={styles.rulesGrid}>
          {[
            { icon: '🌫️', text: '5 hints, image sharpens each round' },
            { icon: '⚡', text: 'Answer in 10s for speed bonus' },
            { icon: '🏆', text: 'Best of 3 rounds wins the match' },
            { icon: '⏭️', text: 'Both agree to skip a hint (5 per game)' },
          ].map((r, i) => (
            <div key={i} style={styles.ruleItem}>
              <span style={styles.ruleIcon}>{r.icon}</span>
              <span style={styles.ruleText}>{r.text}</span>
            </div>
          ))}
        </div>

        {/* Nickname input */}
        <div style={styles.nickSection}>
          <p style={styles.nickLabel}>Your nickname</p>
          <div style={styles.nickRow}>
            <input
              style={styles.nickInput}
              value={nickInput}
              onChange={e => setNickInput(e.target.value.replace(/[^a-zA-Z\s]/g, ''))}
              placeholder="Letters only, no numbers"
              maxLength={16}
              onKeyDown={e => e.key === 'Enter' && handleSaveNick()}
            />
            <button style={styles.nickSaveBtn} onClick={handleSaveNick}>Save</button>
          </div>
          {nickError && <p style={styles.nickError}>{nickError}</p>}
          {nickname && !nickError && (
            <p style={styles.nickConfirm}>Playing as: <strong>{nickname}</strong></p>
          )}
        </div>

        {/* Streak badge */}
        {streak >= 3 && (
          <div style={styles.streakRow}>
            <span>{streakBadge}</span>
            <span style={styles.streakText}>You are on a {streak}-game win streak!</span>
          </div>
        )}

        {/* Player info */}
        {currentUser && (
          <div style={styles.playerInfo}>
            <div style={styles.avatar}>
              {(nickname || currentUser.displayName || currentUser.email)?.[0]?.toUpperCase() || '?'}
            </div>
            <span style={styles.playerName}>
              Playing as <strong>{nickname || currentUser.displayName || currentUser.email?.split('@')[0]}</strong>
            </span>
          </div>
        )}

        <button style={styles.playBtn} onClick={handleJoinQueue}>
          Find Opponent →
        </button>

        <p style={styles.note}>Matches are anonymous · No data saved after game ends</p>
      </div>
    </div>
  );
}

const styles = {
  lobby: { minHeight: '100vh', background: '#0d0d1a', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px', fontFamily: "'Syne', sans-serif" },
  card: { maxWidth: '460px', width: '100%', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '28px', padding: '40px 32px', display: 'flex', flexDirection: 'column', gap: '20px', boxShadow: '0 0 60px rgba(99,102,241,0.1)' },
  logoRow: { display: 'flex', alignItems: 'center', gap: '12px' },
  title: { margin: 0, fontSize: '36px', fontWeight: '900', color: '#fff', letterSpacing: '-1.5px' },
  subtitle: { margin: 0, fontSize: '14px', color: 'rgba(255,255,255,0.45)', lineHeight: '1.5' },
  rulesGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' },
  ruleItem: { display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '12px', borderRadius: '12px', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' },
  ruleIcon: { fontSize: '18px', flexShrink: 0 },
  ruleText: { fontSize: '12px', color: 'rgba(255,255,255,0.6)', lineHeight: '1.4' },
  nickSection: { display: 'flex', flexDirection: 'column', gap: '8px' },
  nickLabel: { margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.4)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px' },
  nickRow: { display: 'flex', gap: '8px' },
  nickInput: { flex: 1, padding: '10px 14px', borderRadius: '12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: '#fff', fontSize: '14px', outline: 'none', fontFamily: "'Syne', sans-serif" },
  nickSaveBtn: { padding: '10px 16px', borderRadius: '12px', background: '#6366f1', border: 'none', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  nickError: { margin: 0, fontSize: '12px', color: '#ef4444' },
  nickConfirm: { margin: 0, fontSize: '12px', color: '#22c55e' },
  streakRow: { display: 'flex', alignItems: 'center', gap: '10px', padding: '12px 16px', borderRadius: '14px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.2)' },
  streakText: { fontSize: '13px', color: '#fbbf24', fontWeight: '600' },
  playerInfo: { display: 'flex', alignItems: 'center', gap: '10px' },
  avatar: { width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px', fontWeight: '800', color: '#fff', flexShrink: 0 },
  playerName: { fontSize: '13px', color: 'rgba(255,255,255,0.5)' },
  playBtn: { width: '100%', padding: '16px', background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', border: 'none', borderRadius: '16px', color: '#fff', fontSize: '17px', fontWeight: '800', cursor: 'pointer', letterSpacing: '-0.3px', boxShadow: '0 4px 24px rgba(99,102,241,0.35)' },
  note: { margin: 0, textAlign: 'center', fontSize: '11px', color: 'rgba(255,255,255,0.2)' },
};